import React, { useState, useCallback, useRef } from "react";
import { Button, Modal, Form, Col, Row, Spinner } from "react-bootstrap";
import { FaTimes, FaPlay, FaStop, FaLink } from "react-icons/fa";
import SpinnerLoader from "./SpinnerLoader";
import SendMarketingEmailConfirmModal from "./SendMarketingEmailConfirmModal";
import CopyDemoLinkModal from "./CopyDemoLinkModal";
import {
  saveGroupContact,
  deleteGroupContact,
  sendMarketingEmail,
  fetchDemoMetadata,
  generatePronunciationAudio,
} from "../Services/MarketingContacts";
import { toast } from "react-toastify";
import { getEmailStatus } from "../utils/emailStatus";
import SubContactsEditor from "./SubContactsEditor";
import {
  emptySubContact,
  formatSubContact,
  getSubContacts,
  isValidEmail,
  swapPrimaryWithSubContact,
} from "../utils/subContacts";

// Every date in this dashboard is read as Pacific time, no matter where the
// browser is. The client is in Seattle and always wants to see it that way, so
// rendering must not follow the viewer's machine. Storage stays UTC ISO.
const PACIFIC_TZ = "America/Los_Angeles";

// Calendar/clock fields for an instant, as they read in Pacific.
const pacificParts = (date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: PACIFIC_TZ,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const out = parts.reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  // Some engines render midnight as "24" under hour12:false
  out.hour = String(Number(out.hour) % 24).padStart(2, "0");
  return out;
};

// How far Pacific sits from UTC at a given instant (-7h in PDT, -8h in PST).
const pacificOffsetMs = (date) => {
  const p = pacificParts(date);
  const asUTC = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second)
  );
  return asUTC - date.getTime();
};

// ISO instant for 00:00 Pacific on the given calendar date. Resolved twice so a
// date sitting on a DST boundary uses the offset actually in effect that day.
const pacificMidnightISO = (year, month, day) => {
  const wall = Date.UTC(year, month - 1, day, 0, 0, 0);
  let t = wall - pacificOffsetMs(new Date(wall));
  t = wall - pacificOffsetMs(new Date(t));
  return new Date(t).toISOString();
};

const formatTimestamp = (date = new Date()) => {
  const p = pacificParts(date);
  return `${p.month}-${p.day}-${p.year} ${p.hour}:${p.minute}`;
};

// Progressive US phone formatter: strips non-digits, caps at 10, formats as
// "555", "555-555", or "555-555-5555" depending on how many digits are present.
const formatPhoneNumber = (input) => {
  const digits = String(input || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const PHONE_TYPES = [
  "Direct Number",
  "Company Number",
  "Mobile",
  "Home",
  "Fax",
  "Other",
];

const prependTimestampedNote = (existingNotes, newText) => {
  const entry = `[${formatTimestamp()}] ${newText.trim()}`;
  return existingNotes ? `${entry}\n\n${existingNotes}` : entry;
};

// Which Edit Contact sections start open. The rest start collapsed but show a
// one-line summary, so nothing is hidden without a hint of what's inside.
const DEFAULT_OPEN_SECTIONS = {
  group: true,
  contact: true,
  others: true,
  location: false,
  followUp: true,
  tracking: false,
  unsubscribed: false,
};

// A collapsible block in the Edit Contact dialog. Defined outside the listing
// so re-renders don't remount it and steal focus from the input being typed in.
const DialogSection = ({ title, summary, open, onToggle, children }) => (
  <div className="border rounded mb-3">
    <Button
      variant="link"
      onClick={onToggle}
      aria-expanded={open}
      className="w-100 text-left d-flex align-items-center px-3 py-2"
      style={{ textDecoration: "none" }}
    >
      <span className="mr-2 text-muted" style={{ width: "1em" }}>
        {open ? "▼" : "►"}
      </span>
      <strong className="text-dark text-nowrap">{title}</strong>
      {!open && summary && (
        <span
          className="text-muted ml-3 text-truncate"
          style={{ fontSize: "0.875rem" }}
        >
          {summary}
        </span>
      )}
    </Button>
    {open && (
      <div className="px-3 pt-3 border-top">
        <Row>{children}</Row>
      </div>
    )}
  </div>
);

export default function MarketingContactsListing({
  contacts = [],
  showSpinner,
  refreshTable,
  onAddNewContact,
  sortKeys = [],
  onHeaderSortClick,
}) {
  // Renders a clickable column header — click sorts by that field, shift-click
  // appends/toggles as an additional key. Non-sortable columns pass sortField=null.
  const SortableHeader = ({ sortField, className, style, children }) => {
    const idx = sortField
      ? sortKeys.findIndex((k) => k.field === sortField)
      : -1;
    const active = idx >= 0;
    const dir = active ? sortKeys[idx].dir : null;
    const arrow = dir === "desc" ? " ↓" : dir === "asc" ? " ↑" : "";
    const clickable = !!(sortField && onHeaderSortClick);
    return (
      <div
        className={className}
        style={{
          ...style,
          cursor: clickable ? "pointer" : undefined,
          userSelect: clickable ? "none" : undefined,
          fontWeight: active ? 700 : undefined,
        }}
        onClick={
          clickable
            ? (e) => onHeaderSortClick(sortField, e.shiftKey)
            : undefined
        }
        title={
          clickable
            ? "Click to sort • Shift+click to add as secondary sort"
            : undefined
        }
      >
        {children}
        {arrow}
      </div>
    );
  };
  // Pagination state
  const PAGE_SIZE = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const [show, setShow] = useState(false);
  const [contactDetails, setContactDetails] = useState({
    group_name: "",
    contact_name: "",
    contact_email: "",
    city: "",
    state: "",
    avg_attend: "",
    denom: "",
    email_sent: false,
    live_contact: true,
    demo_id: "",
    group_name_pronunciation: "",
    org_type: "",
    phone1: "",
    phone1_type: "",
    phone2: "",
    phone2_type: "",
    notes: "",
    date_sent: "",
    follow_up_date: "",
    no_follow_up: false,
    no_follow_up_reason: "",
    meeting: false,
    unsubscribed: false,
    unsubscribed_reason: "",
    unsubscribed_date: "",
    contact_title: "",
    chatgpt_rank: "",
    sub_contacts: [],
  });
  // parses typed MM-DD-YYYY or MM/DD/YYYY into the ISO instant for midnight
  // Pacific on that date; returns null for incomplete/invalid input
  const parseTypedDate = (text) => {
    const match = String(text || "")
      .trim()
      .match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (!match) return null;
    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const iso = pacificMidnightISO(year, month, day);
    if (iso === null) return null;
    return iso;
  };

  // formats ISO timestamps as MM-DD-YYYY in Pacific; "" for missing/invalid
  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const p = pacificParts(d);
    return `${p.month}-${p.day}-${p.year}`;
  };
  const [isEditing, setIsEditing] = useState(false);
  const [dateSentInputText, setDateSentInputText] = useState("");
  const [followUpDateInputText, setFollowUpDateInputText] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [validDemoIds, setValidDemoIds] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [contactToSend, setContactToSend] = useState(null);
  // null for the primary contact, or the sub-contact being emailed.
  const [recipientToSend, setRecipientToSend] = useState(null);
  const [sending, setSending] = useState(false);
  // The contact whose demo links are shown in the Copy Demo Link dialog.
  const [contactForLinks, setContactForLinks] = useState(null);
  const [openSections, setOpenSections] = useState(DEFAULT_OPEN_SECTIONS);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [notesEditable, setNotesEditable] = useState(false);
  // Which action the unsaved-note warning is holding up: "close" or "save".
  const [pendingNoteAction, setPendingNoteAction] = useState(null);
  const [pronunciationTestLoading, setPronunciationTestLoading] = useState(false);
  const [pronunciationAudioUrl, setPronunciationAudioUrl] = useState("");
  const [playingGroupName, setPlayingGroupName] = useState(null);
  const [loadingGroupName, setLoadingGroupName] = useState(null);
  const audioRef = useRef(null);
  const audioCacheRef = useRef(new Map());

  const handleClose = () => {
    setShow(false);
    setContactDetails({
      group_name: "",
      contact_name: "",
      contact_email: "",
      city: "",
      state: "",
      avg_attend: "",
      denom: "",
      email_sent: false,
      live_contact: true,
      demo_id: "",
      group_name_pronunciation: "",
      org_type: "",
      phone1: "",
      phone1_type: "",
      phone2: "",
      phone2_type: "",
      notes: "",
      date_sent: "",
      follow_up_date: "",
      no_follow_up: false,
      no_follow_up_reason: "",
      meeting: false,
      unsubscribed: false,
      unsubscribed_reason: "",
      unsubscribed_date: "",
      contact_title: "",
      chatgpt_rank: "",
      sub_contacts: [],
    });
    setIsEditing(false);
    setErrors({});
    setOpenSections(DEFAULT_OPEN_SECTIONS);
    setShowAddNote(false);
    setNewNoteText("");
    setNotesEditable(false);
    setPendingNoteAction(null);
    setPronunciationAudioUrl("");
    setPronunciationTestLoading(false);
  };

  // A note typed into the Add Note box but never submitted with "Add".
  const hasUnsavedNote = showAddNote && newNoteText.trim() !== "";

  // Anything that closes the dialog goes through here so a typed-but-unadded
  // note gets a warning instead of being silently dropped.
  const requestClose = () => {
    if (hasUnsavedNote) {
      setPendingNoteAction("close");
      return;
    }
    handleClose();
  };

  const handleKeepEditingNote = () => setPendingNoteAction(null);

  const handleDiscardNote = () => {
    const action = pendingNoteAction;
    setPendingNoteAction(null);
    setNewNoteText("");
    setShowAddNote(false);
    if (action === "close") {
      handleClose();
    } else if (action === "save") {
      performSaveContact();
    }
  };

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Required fields live in these two; open them so a validation error is
  // never hidden inside a collapsed section.
  const openRequiredSections = () =>
    setOpenSections((prev) => ({
      ...prev,
      group: true,
      contact: true,
      others: true,
    }));

  const handlePronunciationTest = async () => {
    const textToTest =
      contactDetails.group_name_pronunciation || contactDetails.group_name;
    if (!textToTest.trim()) {
      toast.error("Please enter a group name or pronunciation first.");
      return;
    }
    setPronunciationTestLoading(true);
    setPronunciationAudioUrl("");
    try {
      const audioUrl = await generatePronunciationAudio(textToTest);
      setPronunciationAudioUrl(audioUrl);
    } catch (error) {
      console.error("Error generating pronunciation audio:", error);
      toast.error("Failed to generate pronunciation audio. Please try again.");
    } finally {
      setPronunciationTestLoading(false);
    }
  };

  const playContactPronunciation = async (contact) => {
    const textToTest =
      contact.group_name_pronunciation?.trim() || contact.group_name;
    if (!textToTest?.trim()) return;

    const key = contact.group_name;
    const audioEl = audioRef.current;
    if (!audioEl) return;

    if (playingGroupName === key) {
      audioEl.pause();
      audioEl.currentTime = 0;
      setPlayingGroupName(null);
      return;
    }

    if (playingGroupName) audioEl.pause();

    try {
      let url = audioCacheRef.current.get(key);
      if (!url) {
        setLoadingGroupName(key);
        url = await generatePronunciationAudio(textToTest);
        audioCacheRef.current.set(key, url);
      }
      audioEl.src = url;
      await audioEl.play();
      setPlayingGroupName(key);
    } catch (error) {
      console.error("Error playing pronunciation:", error);
      toast.error("Failed to play pronunciation.");
    } finally {
      setLoadingGroupName(null);
    }
  };

  const handleAddNoteSave = async () => {
    if (!newNoteText.trim()) return;

    const updatedContact = {
      ...contactDetails,
      notes: prependTimestampedNote(contactDetails.notes, newNoteText),
    };

    if (
      !updatedContact.group_name.trim() ||
      !updatedContact.contact_name.trim() ||
      !updatedContact.contact_email.trim() ||
      !/\S+@\S+\.\S+/.test(updatedContact.contact_email)
    ) {
      openRequiredSections();
      toast.error("Please fill in required fields before adding a note.");
      return;
    }

    setContactDetails(updatedContact);
    setNewNoteText("");
    setShowAddNote(false);
    setSaving(true);
    try {
      const toSave = { ...updatedContact };
      if (!toSave.group_name_pronunciation) {
        toSave.group_name_pronunciation = toSave.group_name;
      }
      await saveGroupContact(toSave);
      toast.success("Note added.");
      refreshTable();
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error("Error saving note. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNoteCancel = () => {
    setNewNoteText("");
    setShowAddNote(false);
  };

  const handleShow = () => setShow(true);

  const editContactHandler = (contact) => {
    setContactDetails({
      ...contact,
      contact_name: contact.contact_name === "Unknown" ? "" : contact.contact_name,
      contact_email: contact.contact_email === "Unknown" ? "" : contact.contact_email,
      city: contact.city === "Unknown" ? "" : contact.city || "",
      state: contact.state === "Unknown" ? "" : contact.state || "",
      avg_attend:
        contact.avg_attend === "Unknown" ? "" : contact.avg_attend || "",
      denom: contact.denom === "Unknown" ? "" : contact.denom || "",
      phone1: contact.phone1 || "",
      phone1_type: contact.phone1_type || "",
      phone2: contact.phone2 || "",
      phone2_type: contact.phone2_type || "",
      contact_title: contact.contact_title || "",
      chatgpt_rank: contact.chatgpt_rank || "",
      // Fill in any field an older record lacks so the editor can rely on
      // every one being a string.
      sub_contacts: getSubContacts(contact).map((s) => ({
        ...emptySubContact(),
        ...s,
      })),
    });
    setIsEditing(true);
    handleShow();
  };

  const addNewContactHandler = useCallback(() => {
    setContactDetails({
      group_name: "",
      contact_name: "",
      contact_email: "",
      city: "",
      state: "",
      avg_attend: "",
      denom: "",
      email_sent: false,
      live_contact: true,
      demo_id: "",
      group_name_pronunciation: "",
      org_type: "",
      phone1: "",
      phone1_type: "",
      phone2: "",
      phone2_type: "",
      notes: "",
      date_sent: "",
      follow_up_date: "",
      no_follow_up: false,
      no_follow_up_reason: "",
      meeting: false,
      unsubscribed: false,
      unsubscribed_reason: "",
      unsubscribed_date: "",
      contact_title: "",
      chatgpt_rank: "",
      sub_contacts: [],
    });
    setIsEditing(false);
    handleShow();
  }, []);

  // Expose the addNewContactHandler to parent
  React.useEffect(() => {
    if (
      onAddNewContact &&
      typeof onAddNewContact === "object" &&
      onAddNewContact.current !== undefined
    ) {
      onAddNewContact.current = addNewContactHandler;
    }
  }, [onAddNewContact, addNewContactHandler]);

  // Prime / clear the typed-date input whenever the modal opens or closes
  React.useEffect(() => {
    if (show) {
      setDateSentInputText(formatDate(contactDetails.date_sent));
      setFollowUpDateInputText(formatDate(contactDetails.follow_up_date));
    } else {
      setDateSentInputText("");
      setFollowUpDateInputText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Validate demo metadata for contacts with demo_id
  React.useEffect(() => {
    const validateDemoMetadata = async () => {
      const newValidDemoIds = new Set();

      for (const contact of contacts) {
        if (contact.demo_id) {
          try {
            await fetchDemoMetadata(contact.demo_id);
            newValidDemoIds.add(contact.demo_id);
          } catch (error) {
            console.warn(
              `Demo metadata not found for demo_id: ${contact.demo_id}`
            );
            // Don't add to validDemoIds set
          }
        }
      }

      setValidDemoIds(newValidDemoIds);
    };

    if (contacts.length > 0) {
      validateDemoMetadata();
    }

    // Reset to first page when contacts change
    setCurrentPage(1);
  }, [contacts]);

  const CHECKBOX_FIELDS = ["email_sent", "live_contact", "no_follow_up", "meeting", "unsubscribed"];

  const handleInputChange = (field) => (e) => {
    const value = CHECKBOX_FIELDS.includes(field) ? e.target.checked : e.target.value;
    setContactDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // "No follow-up needed" is mutually exclusive with a follow-up date: checking
  // it clears the date + meeting; unchecking it clears the reason.
  const handleNoFollowUpChange = (e) => {
    const checked = e.target.checked;
    setContactDetails((prev) => ({
      ...prev,
      no_follow_up: checked,
      ...(checked
        ? { follow_up_date: "", meeting: false }
        : { no_follow_up_reason: "" }),
    }));
    if (checked) setFollowUpDateInputText("");
  };

  // Unchecking "Unsubscribed" clears the reason; the server clears the date.
  const handleUnsubscribedChange = (e) => {
    const checked = e.target.checked;
    setContactDetails((prev) => ({
      ...prev,
      unsubscribed: checked,
      ...(checked ? {} : { unsubscribed_reason: "", unsubscribed_date: "" }),
    }));
  };

  const handleFollowUpDateChange = (e) => {
    const text = e.target.value;
    setFollowUpDateInputText(text);
    const parsed = parseTypedDate(text);
    if (parsed) {
      setContactDetails((prev) => ({ ...prev, follow_up_date: parsed }));
    } else if (text.trim() === "") {
      setContactDetails((prev) => ({ ...prev, follow_up_date: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!contactDetails.group_name.trim()) {
      newErrors.group_name = "Group name is required";
    }

    if (!contactDetails.contact_name.trim()) {
      newErrors.contact_name = "Contact name is required";
    }

    if (!contactDetails.contact_email.trim()) {
      newErrors.contact_email = "Contact email is required";
    } else if (!/\S+@\S+\.\S+/.test(contactDetails.contact_email)) {
      newErrors.contact_email = "Contact email is invalid";
    }

    getSubContacts(contactDetails).forEach((s) => {
      if (!s.name.trim() && !s.email.trim()) {
        newErrors[`sub_${s.id}_name`] = "Add a name or email, or remove this contact";
      }
      if (s.email.trim() && !isValidEmail(s.email)) {
        newErrors[`sub_${s.id}_email`] = "Email is invalid";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveContactHandler = () => {
    // Saving the contact does not pick up an unadded note, and it closes the
    // dialog on success, so warn before that note disappears.
    if (hasUnsavedNote) {
      setPendingNoteAction("save");
      return;
    }
    performSaveContact();
  };

  const performSaveContact = async () => {
    if (!validateForm()) {
      openRequiredSections();
      return;
    }

    setSaving(true);
    try {
      if (
        contactDetails.group_name_pronunciation === undefined ||
        contactDetails.group_name_pronunciation === ""
      ) {
        contactDetails.group_name_pronunciation = contactDetails.group_name;
      }
      await saveGroupContact(contactDetails);
      toast.success(
        isEditing
          ? "Contact updated successfully!"
          : "Contact saved successfully!"
      );
      handleClose();
      refreshTable();
    } catch (error) {
      console.error("Error saving contact:", error);
      toast.error("Error saving contact. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleViewDemo = (demo_id) => {
    const demoUrl = `https://demo.jobinfonetwork.com/#/demo/${demo_id}?source=admin`;
    window.open(demoUrl, "_blank");
  };

  const handleSendClick = (contact) => {
    if (!contact.demo_id) {
      toast.error("No demo ID available for this contact");
      return;
    }
    // The Send button is disabled for these, but never email an unsubscribed
    // contact even if something else reaches this handler.
    if (contact.unsubscribed) {
      toast.error("This contact has unsubscribed");
      return;
    }
    setContactToSend(contact);
    setShowSendConfirm(true);
  };

  // Send from a sub-contact's card in the dialog. Recording the send saves
  // the whole contact, so the form must be valid first.
  const handleSubSendClick = (subContact) => {
    if (!validateForm()) {
      openRequiredSections();
      toast.error("Fix the highlighted fields before sending.");
      return;
    }
    if (subContact.unsubscribed) {
      toast.error("This person has unsubscribed");
      return;
    }
    setContactToSend(contactDetails);
    setRecipientToSend(subContact);
    setShowSendConfirm(true);
  };

  // Send from the Primary Contact section of the dialog. Like a sub-contact
  // send, it saves the whole contact, so the form must be valid first.
  const handlePrimarySendClick = () => {
    if (!validateForm()) {
      openRequiredSections();
      toast.error("Fix the highlighted fields before sending.");
      return;
    }
    if (contactDetails.unsubscribed) {
      toast.error("This contact has unsubscribed");
      return;
    }
    setContactToSend(contactDetails);
    setRecipientToSend(null);
    setShowSendConfirm(true);
  };

  const handleSendCancel = () => {
    setShowSendConfirm(false);
    setContactToSend(null);
    setRecipientToSend(null);
  };

  const handleSendConfirm = async () => {
    if (!contactToSend) return;
    setSending(true);
    try {
      if (recipientToSend) {
        await handleSendToSubContact(contactToSend, recipientToSend);
      } else {
        await handleSendDemo(contactToSend);
      }
      setShowSendConfirm(false);
      setContactToSend(null);
      setRecipientToSend(null);
    } finally {
      setSending(false);
    }
  };

  // Sub-contact sends get no delivery tracking (that belongs to the primary,
  // and the server leaves it alone when sub_contact_id is set). They record
  // the person's date_sent and a note, and leave email_sent untouched.
  const handleSendToSubContact = async (contact, subContact) => {
    const email = subContact.email.trim();
    const who = subContact.name.trim()
      ? `${subContact.name.trim()} <${email}>`
      : email;
    try {
      const metadata = await fetchDemoMetadata(contact.demo_id);
      toast.info("Sending marketing email...");
      const result = await sendMarketingEmail({
        to_group_name: contact.group_name,
        to_email: email,
        to_name: subContact.name.trim(),
        video_url: `https://demo.jobinfonetwork.com/#/demo/${contact.demo_id}`,
        video_youtube_url: metadata.youtube_video_url,
        sub_contact_id: subContact.id,
      });
      const failed = result && result.success === false;
      const sentAt = new Date().toISOString();
      const updated = {
        ...contact,
        notes: prependTimestampedNote(
          contact.notes,
          failed
            ? `Email to ${who} failed to send: ${result.message}`
            : `Email sent to ${who}`
        ),
        sub_contacts: failed
          ? getSubContacts(contact)
          : getSubContacts(contact).map((s) =>
            s.id === subContact.id ? { ...s, date_sent: sentAt } : s
          ),
      };
      if (!updated.group_name_pronunciation) {
        updated.group_name_pronunciation = updated.group_name;
      }
      await saveGroupContact(updated);
      setContactDetails((prev) => ({
        ...prev,
        notes: updated.notes,
        sub_contacts: updated.sub_contacts,
      }));
      if (failed) {
        toast.error(`Email failed to send: ${result.message}`);
      } else {
        toast.success(`Marketing email sent to ${who}`);
      }
      refreshTable();
    } catch (error) {
      console.error("Error sending to sub-contact:", error);
      toast.error(`Error sending demo: ${error.message}`);
    }
  };

  const handleMakePrimary = (subContact) => {
    setContactDetails((prev) => swapPrimaryWithSubContact(prev, subContact.id));
    setErrors({});
    toast.info(
      `${subContact.name.trim() || subContact.email.trim()} is now the primary contact. Save to keep this change.`
    );
  };

  const clearError = (key) => {
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  // Why a Send button inside the dialog (primary or sub-contact) is disabled.
  const dialogSendDisabledReason = !isEditing
    ? "Save the contact first"
    : !contactDetails.demo_id
      ? "No demo for this contact yet"
      : !validDemoIds.has(contactDetails.demo_id)
        ? "Demo metadata not available"
        : "";

  const primarySendBlocked =
    dialogSendDisabledReason ||
    (contactDetails.unsubscribed && "This contact has unsubscribed") ||
    (!contactDetails.contact_email.trim() && "Add an email address to send");

  // When the person in the pending send was last emailed, so the confirm
  // dialog can warn before a resend. "" if never sent.
  const lastSentLabel = (contact, recipient) => {
    if (!contact) return "";
    if (recipient) return formatDate(recipient.date_sent);
    if (!contact.email_sent) return "";
    return formatDate(contact.date_sent) || "an unknown date";
  };

  const handleSendDemo = async (contact) => {
    if (!contact.demo_id) {
      toast.error("No demo ID available for this contact");
      return;
    }

    try {
      // Load demo metadata
      const metadata = await fetchDemoMetadata(contact.demo_id);

      // Send marketing email
      toast.info("Sending marketing email...");
      const emailPayload = {
        to_group_name: contact.group_name,
        to_email: contact.contact_email,
        to_name: contact.contact_name,
        video_url: `https://demo.jobinfonetwork.com/#/demo/${contact.demo_id}`,
        video_youtube_url: metadata.youtube_video_url,
      };

      const result = await sendMarketingEmail(emailPayload);

      // The server records the failure on the contact; leave email_sent
      // alone and note the attempt so the history shows it. Checked with
      // === false so an older server that doesn't return success still works.
      if (result && result.success === false) {
        toast.error(`Email failed to send: ${result.message}`);
        const failedNotes = prependTimestampedNote(
          contact.notes,
          `Email failed to send: ${result.message}`
        );
        await saveGroupContact({ ...contact, notes: failedNotes });
        setContactDetails((prev) => ({ ...prev, notes: failedNotes }));
        refreshTable();
        return;
      }

      toast.success("Marketing email sent successfully!");

      // Mark contact as email sent and add note. date_sent has to be stamped
      // here: save_group_metadata only preserves an incoming value, it never
      // sets one, so without this the spread carries the contact's old
      // date_sent back (empty on a first send, stale on a resend).
      const updatedNotes = prependTimestampedNote(
        contact.notes,
        "Email sent"
      );
      const sentAt = new Date().toISOString();

      const updated = {
        ...contact,
        email_sent: true,
        date_sent: sentAt,
        notes: updatedNotes,
      };
      if (!updated.group_name_pronunciation) {
        updated.group_name_pronunciation = updated.group_name;
      }
      await saveGroupContact(updated);
      setContactDetails((prev) => ({
        ...prev,
        email_sent: true,
        date_sent: sentAt,
        notes: updatedNotes,
      }));

      // Optionally refresh the table to update email status
      refreshTable();
    } catch (error) {
      console.error("Error sending demo:", error);
      toast.error(`Error sending demo: ${error.message}`);
    }
  };

  const handleDeleteClick = (contact) => {
    setContactToDelete(contact);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;

    setDeleting(true);
    try {
      await deleteGroupContact(contactToDelete.group_name);
      toast.success("Contact deleted successfully!");
      setShowDeleteConfirm(false);
      setContactToDelete(null);
      refreshTable();
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error("Error deleting contact. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setContactToDelete(null);
  };

  return (
    <React.Fragment>
      <audio
        ref={audioRef}
        onEnded={() => setPlayingGroupName(null)}
        style={{ display: "none" }}
      />
      <ul className="job-list support-team-list mobile-list">
        <li className="headings">
          <SortableHeader sortField="group_name" className="m-width2">
            Group Name
          </SortableHeader>
          <SortableHeader sortField="contact_name" className="m-width2">
            Contact Name
          </SortableHeader>
          <SortableHeader
            sortField="contact_email"
            className="m-width2 d-none d-xl-block"
            style={{ width: 220, flex: "0 0 220px" }}
          >
            Contact Email
          </SortableHeader>
          <SortableHeader sortField={null} className="m-width2">
            Demo
          </SortableHeader>
          <SortableHeader sortField="email_sent" className="m-width2">
            Email Status
          </SortableHeader>
          <SortableHeader
            sortField="state"
            className="m-width2 d-none d-xl-block"
          >
            State
          </SortableHeader>
          <SortableHeader
            sortField="chatgpt_rank"
            className="m-width2 d-none d-xl-block"
          >
            ChatGPT Rank
          </SortableHeader>
          <SortableHeader
            sortField="view_count_direct"
            className="m-width2 d-none d-xl-block"
          >
            Prospect Views
          </SortableHeader>
          <SortableHeader
            sortField="last_viewed_direct"
            className="m-width2 d-none d-xl-block"
          >
            Prospect Last Viewed
          </SortableHeader>
          <SortableHeader
            sortField="date_sent"
            className="m-width2 d-none d-xl-block"
          >
            Most Recent Date Sent
          </SortableHeader>
          <SortableHeader
            sortField="follow_up_date"
            className="m-width2 d-none d-xl-block"
          >
            Follow-up
          </SortableHeader>
          <SortableHeader sortField={null} className="m-width4 text-right">
            Action
          </SortableHeader>
        </li>
        {showSpinner && <SpinnerLoader />}
        {contacts.length === 0 && !showSpinner && (
          <h5 style={{ textAlign: "center" }}>No Data Found</h5>
        )}
        {/** Render only the contacts for the current page to limit DOM size */}
        {contacts
          .slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
          .map((contact, index) => {
            const hasPron = !!(
              contact.group_name_pronunciation?.trim() ||
              contact.group_name?.trim()
            );
            const isLoading = loadingGroupName === contact.group_name;
            const isPlaying = playingGroupName === contact.group_name;
            return (
              <li key={index} className="align-items-center">
                <div className="m-width2 mobile-column">
                  <span className="mobile-column-name">Group Name</span>
                  <span className="mobile-column-content d-flex align-items-center">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => playContactPronunciation(contact)}
                      disabled={isLoading || !hasPron}
                      title={
                        !hasPron
                          ? "No group name or pronunciation set"
                          : isPlaying
                            ? "Stop"
                            : "Play pronunciation"
                      }
                      className="p-0 mr-2 d-inline-flex align-items-center justify-content-center"
                      style={{
                        width: 24,
                        height: 24,
                        lineHeight: 1,
                        textDecoration: "none",
                      }}
                    >
                      {isLoading ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : isPlaying ? (
                        <FaStop size={12} />
                      ) : (
                        <FaPlay size={12} />
                      )}
                    </Button>
                    {contact.group_name}
                  </span>
                  {contact.unsubscribed && (
                    <span
                      className="d-block text-danger"
                      title={
                        contact.unsubscribed_reason ||
                        "Marked as unsubscribed"
                      }
                    >
                      Unsubscribed
                      {formatDate(contact.unsubscribed_date)
                        ? ` (${formatDate(contact.unsubscribed_date)})`
                        : ""}
                    </span>
                  )}
                </div>
                <div className="m-width2 mobile-column">
                  <span className="mobile-column-name">Contact Name</span>
                  <span className="mobile-column-content">
                    {contact.contact_name}
                    {getSubContacts(contact).length > 0 && (
                      <span
                        className="badge bg-light text-dark border ml-1"
                        title={getSubContacts(contact)
                          .map(formatSubContact)
                          .join("\n")}
                      >
                        +{getSubContacts(contact).length}
                      </span>
                    )}
                  </span>
                </div>
                <div
                  className="m-width2 mobile-column d-none d-xl-block"
                  style={{ width: 220, flex: "0 0 220px", minWidth: 0 }}
                >
                  <span className="mobile-column-name">Contact Email</span>
                  <span
                    className="mobile-column-content d-block text-truncate"
                    title={contact.contact_email}
                  >
                    {contact.contact_email}
                  </span>
                </div>
                <div className="m-width2 mobile-column">
                  <span className="mobile-column-name">Demo</span>
                  <span className="mobile-column-content">
                    {contact.demo_id && validDemoIds.has(contact.demo_id)
                      ? "Created"
                      : contact.demo_id
                        ? "Pending"
                        : "N/A"}
                  </span>
                  {contact.demo_id && (
                    <div className="d-flex flex-column mobile-column seeker-action-btns text-md-right status small-action-btn">
                      <Button
                        type="button"
                        className="btn btn-info btn-sm mb-1"
                        onClick={() => handleViewDemo(contact.demo_id)}
                        title={
                          validDemoIds.has(contact.demo_id)
                            ? "View Demo"
                            : "Demo metadata not available"
                        }
                        disabled={!validDemoIds.has(contact.demo_id)}
                      >
                        <span className="icon-eye mr-1"></span>View
                      </Button>
                      <Button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => handleSendClick(contact)}
                        title={
                          contact.unsubscribed
                            ? "Contact has unsubscribed"
                            : validDemoIds.has(contact.demo_id)
                              ? "Send Demo"
                              : "Demo metadata not available"
                        }
                        disabled={
                          !validDemoIds.has(contact.demo_id) ||
                          contact.email_sent ||
                          !!contact.unsubscribed
                        }
                      >
                        <span className="icon-mail mr-1"></span>Send
                      </Button>
                      <Button
                        type="button"
                        className="btn btn-secondary btn-sm mt-1"
                        onClick={() => setContactForLinks(contact)}
                        title={
                          validDemoIds.has(contact.demo_id)
                            ? "Copy a demo link to send yourself"
                            : "Demo metadata not available"
                        }
                        disabled={!validDemoIds.has(contact.demo_id)}
                      >
                        Links
                      </Button>
                    </div>
                  )}
                </div>
                <div className="m-width2 mobile-column">
                  <span className="mobile-column-name">Email Status</span>
                  {(() => {
                    const status = getEmailStatus(contact);
                    const when = formatDate(contact.email_status_date);
                    const tooltip = [status.detail, when && `Updated ${when}`]
                      .filter(Boolean)
                      .join("\n");
                    return (
                      <span
                        className="mobile-column-content text-nowrap fixed-width"
                        title={tooltip || undefined}
                      >
                        <span className={`badge ${status.badgeClass}`}>
                          {status.label}
                        </span>
                        {status.problem && status.detail && (
                          <small
                            className="d-block text-danger"
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "12rem",
                            }}
                          >
                            {status.detail}
                          </small>
                        )}
                      </span>
                    );
                  })()}
                </div>
                <div className="m-width2 mobile-column d-none d-xl-block">
                  <span className="mobile-column-name">State</span>
                  <span className="mobile-column-content">
                    {contact.state || ""}
                  </span>
                </div>
                <div className="m-width2 mobile-column d-none d-xl-block">
                  <span className="mobile-column-name">ChatGPT Rank</span>
                  <span className="mobile-column-content">
                    {contact.chatgpt_rank || ""}
                  </span>
                </div>
                <div className="m-width2 mobile-column d-none d-xl-block">
                  <span className="mobile-column-name">Prospect Views</span>
                  <span className="mobile-column-content">
                    {contact.view_count_direct ?? contact.view_count ?? 0}
                  </span>
                </div>
                <div className="m-width2 mobile-column d-none d-xl-block">
                  <span className="mobile-column-name">Prospect Last Viewed</span>
                  {/* Only set once a prospect view has been recorded since
                      last_viewed_direct started being tracked; older contacts
                      have a count but no timestamp. */}
                  <span className="mobile-column-content text-nowrap">
                    {formatDate(contact.last_viewed_direct) || "\u2014"}
                  </span>
                </div>
                <div className="m-width2 mobile-column d-none d-xl-block">
                  <span className="mobile-column-name">Most Recent Date Sent</span>
                  <span className="mobile-column-content">
                    {formatDate(contact.date_sent) ||
                      (contact.email_sent ? "Unknown" : "—")}
                  </span>
                </div>
                <div className="m-width2 mobile-column d-none d-xl-block">
                  <span className="mobile-column-name">Follow-up</span>
                  <span className="mobile-column-content">
                    {contact.no_follow_up
                      ? "none"
                      : formatDate(contact.follow_up_date)}
                  </span>
                </div>
                <div className="m-width4 seeker-action-btns text-md-right status small-action-btn">
                  <div className="d-flex flex-column mobile-column">
                    <Button
                      type="button"
                      className="btn btn-primary btn-sm mb-1"
                      onClick={() => editContactHandler(contact)}
                    >
                      <span className="icon-edit mr-2"></span>Edit
                    </Button>
                    <Button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteClick(contact)}
                    >
                      <span className="icon-trash mr-2"></span>Delete
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        {/* Pagination controls - always render, even when not needed */}
        <li className="align-items-center">
          <div className="m-width12 w-100 d-flex justify-content-between align-items-center">
            <div>
              {contacts.length > 0 ? (
                <>
                  Showing{" "}
                  {Math.min((currentPage - 1) * PAGE_SIZE + 1, contacts.length)}{" "}
                  to {Math.min(currentPage * PAGE_SIZE, contacts.length)} of{" "}
                  {contacts.length}
                </>
              ) : (
                <>No contacts</>
              )}
            </div>
            <div>
              <Button
                className="btn btn-light btn-sm mr-2"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>
              <Button
                className="btn btn-light btn-sm"
                disabled={currentPage * PAGE_SIZE >= contacts.length}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </li>
      </ul>

      {/* Add Contact Modal */}
      <Modal
        show={show}
        onHide={requestClose}
        centered
        size="xl"
        className="custome-modal"
      >
        <div className="message-container custome-msg-container">
          <div className="message-header-box">
            <h4 className="text-truncate">
              {isEditing
                ? `Edit Contact${contactDetails.group_name ? ` — ${contactDetails.group_name}` : ""}`
                : "Add New Contact"}
            </h4>
            <div className="messages-modal-actions">
              <Button className="btn" onClick={requestClose}>
                <FaTimes />
              </Button>
            </div>
          </div>
          <div className="message-body">
            <form>
              <Row>
                <Col lg={7}>
                  <DialogSection
                    title="Group"
                    summary={[
                      contactDetails.group_name,
                      contactDetails.org_type,
                      contactDetails.chatgpt_rank &&
                      `ChatGPT rank ${contactDetails.chatgpt_rank}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    open={openSections.group}
                    onToggle={() => toggleSection("group")}
                  >
                    <Col md={12}>
                      <Form.Group controlId="groupName">
                        <Form.Label>
                          Group Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Group Name"
                          className="inputclass form-control"
                          value={contactDetails.group_name}
                          onChange={handleInputChange("group_name")}
                          isInvalid={!!errors.group_name}
                          disabled={isEditing}
                          readOnly={isEditing}
                        />
                        {errors.group_name && (
                          <span style={{ color: "red" }}>{errors.group_name}</span>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={8}>
                      <Form.Group controlId="orgType">
                        <Form.Label>Organization Type</Form.Label>
                        <Form.Control
                          as="select"
                          className="inputclass form-control"
                          value={contactDetails.org_type}
                          onChange={handleInputChange("org_type")}
                        >
                          <option value="">
                            Select Organization Type
                          </option>
                          <option value="Church">Church</option>
                          <option value="School">School</option>
                          <option value="College">College</option>
                          <option value="Organization">Organization</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group controlId="chatgptRank">
                        <Form.Label>ChatGPT Rank</Form.Label>
                        <Form.Control
                          type="text"
                          maxLength={10}
                          placeholder="Rank"
                          className="inputclass form-control"
                          value={contactDetails.chatgpt_rank || ""}
                          onChange={handleInputChange("chatgpt_rank")}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group controlId="contactNamePronunciation">
                        <Form.Label>Group Name Pronunciation</Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="text"
                            placeholder="Group Name Pronunciation"
                            className="inputclass form-control"
                            value={contactDetails.group_name_pronunciation}
                            onChange={handleInputChange("group_name_pronunciation")}
                          />
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="ml-2 text-nowrap"
                            onClick={handlePronunciationTest}
                            disabled={
                              pronunciationTestLoading ||
                              (!contactDetails.group_name.trim() &&
                                !(
                                  contactDetails.group_name_pronunciation || ""
                                ).trim())
                            }
                          >
                            {pronunciationTestLoading ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="mr-2"
                                />
                                Generating Audio...
                              </>
                            ) : (
                              "Test Pronunciation"
                            )}
                          </Button>
                        </div>
                        {pronunciationAudioUrl && (
                          <div className="mt-2">
                            <audio
                              controls
                              src={pronunciationAudioUrl}
                              className="w-100"
                              style={{ maxWidth: "400px" }}
                            >
                              Your browser does not support the audio element.
                            </audio>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </DialogSection>

                  <DialogSection
                    title="Primary Contact"
                    summary={[
                      contactDetails.contact_name,
                      contactDetails.contact_email,
                      contactDetails.phone1,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    open={openSections.contact}
                    onToggle={() => toggleSection("contact")}
                  >
                    <Col md={6} lg={12} xl={6}>
                      <Form.Group controlId="contactName">
                        <Form.Label>
                          Contact Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Contact Name"
                          className="inputclass form-control"
                          value={contactDetails.contact_name}
                          onChange={handleInputChange("contact_name")}
                          isInvalid={!!errors.contact_name}
                        />
                        {errors.contact_name && (
                          <span style={{ color: "red" }}>
                            {errors.contact_name}
                          </span>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6} lg={12} xl={6}>
                      <Form.Group controlId="contactEmail">
                        <Form.Label>
                          Contact Email <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Contact Email"
                          className="inputclass form-control"
                          value={contactDetails.contact_email}
                          onChange={handleInputChange("contact_email")}
                          isInvalid={!!errors.contact_email}
                        />
                        {errors.contact_email && (
                          <span style={{ color: "red" }}>
                            {errors.contact_email}
                          </span>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group controlId="contactTitle">
                        <Form.Label>Role / Title</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. Lead Pastor"
                          className="inputclass form-control"
                          value={contactDetails.contact_title || ""}
                          onChange={handleInputChange("contact_title")}
                        />
                      </Form.Group>
                    </Col>
                    {[1, 2].map((n) => (
                      <Col md={12} key={n}>
                        <Form.Group>
                          <Form.Label htmlFor={`phone${n}`}>Phone {n}</Form.Label>
                          <div className="d-flex">
                            <Form.Control
                              as="select"
                              id={`phone${n}Type`}
                              aria-label={`Phone ${n} type`}
                              className="inputclass form-control mr-2"
                              style={{ flex: "0 0 12rem" }}
                              value={contactDetails[`phone${n}_type`] || ""}
                              onChange={handleInputChange(`phone${n}_type`)}
                            >
                              <option value="">Select Type</option>
                              {PHONE_TYPES.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </Form.Control>
                            <Form.Control
                              type="tel"
                              id={`phone${n}`}
                              placeholder="Phone Number"
                              className="inputclass form-control"
                              value={contactDetails[`phone${n}`] || ""}
                              onChange={(e) => {
                                const value = formatPhoneNumber(e.target.value);
                                setContactDetails((prev) => ({
                                  ...prev,
                                  [`phone${n}`]: value,
                                }));
                              }}
                            />
                          </div>
                        </Form.Group>
                      </Col>
                    ))}
                    <Col md={12} className="d-flex align-items-center mb-3">
                      <span
                        title={
                          primarySendBlocked ||
                          "Send the marketing email to the primary contact"
                        }
                      >
                        <Button
                          variant="success"
                          size="sm"
                          className="mr-2"
                          onClick={handlePrimarySendClick}
                          disabled={!!primarySendBlocked}
                          style={
                            primarySendBlocked
                              ? { pointerEvents: "none" }
                              : undefined
                          }
                        >
                          <span className="icon-mail mr-1"></span>Send
                        </Button>
                      </span>
                      {contactDetails.unsubscribed && (
                        <span className="badge bg-danger text-white mr-2">
                          Unsubscribed
                        </span>
                      )}
                      {contactDetails.email_sent && (
                        <span className="text-muted">
                          Last Sent{" "}
                          {formatDate(contactDetails.date_sent) || "(date unknown)"}
                        </span>
                      )}
                    </Col>
                  </DialogSection>

                  <DialogSection
                    title={`Other Contacts (${getSubContacts(contactDetails).length})`}
                    summary={getSubContacts(contactDetails)
                      .map((s) => s.name || s.email)
                      .filter(Boolean)
                      .join(", ")}
                    open={openSections.others}
                    onToggle={() => toggleSection("others")}
                  >
                    <SubContactsEditor
                      subContacts={getSubContacts(contactDetails)}
                      onChange={(next) =>
                        setContactDetails((prev) => ({
                          ...prev,
                          sub_contacts: next,
                        }))
                      }
                      errors={errors}
                      onClearError={clearError}
                      phoneTypes={PHONE_TYPES}
                      formatPhoneNumber={formatPhoneNumber}
                      formatDate={formatDate}
                      onSend={handleSubSendClick}
                      sendDisabledReason={dialogSendDisabledReason}
                      onMakePrimary={handleMakePrimary}
                    />
                  </DialogSection>

                  <DialogSection
                    title="Location & Details"
                    summary={[
                      [contactDetails.city, contactDetails.state]
                        .filter(Boolean)
                        .join(", "),
                      contactDetails.avg_attend &&
                      `Avg attend ${contactDetails.avg_attend}`,
                      contactDetails.denom,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    open={openSections.location}
                    onToggle={() => toggleSection("location")}
                  >
                    <Col md={6} xl={3}>
                      <Form.Group controlId="city">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="City"
                          className="inputclass form-control"
                          value={contactDetails.city || ""}
                          onChange={handleInputChange("city")}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} xl={3}>
                      <Form.Group controlId="state">
                        <Form.Label>State</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="State"
                          className="inputclass form-control"
                          value={contactDetails.state || ""}
                          onChange={handleInputChange("state")}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} xl={3}>
                      <Form.Group controlId="avgAttend">
                        <Form.Label>Avg Attend</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Avg Attend"
                          className="inputclass form-control"
                          value={contactDetails.avg_attend || ""}
                          onChange={handleInputChange("avg_attend")}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} xl={3}>
                      <Form.Group controlId="denom">
                        <Form.Label>Denomination</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Denomination"
                          className="inputclass form-control"
                          value={contactDetails.denom || ""}
                          onChange={handleInputChange("denom")}
                        />
                      </Form.Group>
                    </Col>
                  </DialogSection>

                  <DialogSection
                    title="Follow-up"
                    summary={
                      contactDetails.no_follow_up
                        ? "No follow-up needed"
                        : [
                          formatDate(contactDetails.follow_up_date) ||
                          "No date set",
                          contactDetails.meeting && "Meeting",
                        ]
                          .filter(Boolean)
                          .join(" · ")
                    }
                    open={openSections.followUp}
                    onToggle={() => toggleSection("followUp")}
                  >
                    <Col md={12}>
                      <Form.Group controlId="noFollowUp">
                        <Form.Check
                          type="checkbox"
                          label="No follow-up needed"
                          checked={!!contactDetails.no_follow_up}
                          onChange={handleNoFollowUpChange}
                        />
                      </Form.Group>
                    </Col>
                    {contactDetails.no_follow_up ? (
                      <Col md={12}>
                        <Form.Group controlId="noFollowUpReason">
                          <Form.Label>Why is no follow-up needed?</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Explain why no follow-up is needed"
                            className="inputclass form-control"
                            value={contactDetails.no_follow_up_reason || ""}
                            onChange={handleInputChange("no_follow_up_reason")}
                          />
                        </Form.Group>
                      </Col>
                    ) : (
                      <>
                        <Col md={8}>
                          <Form.Group controlId="followUpDate">
                            <Form.Label>Next Follow-up Date</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="MM-DD-YYYY Time (optional)"
                              className="inputclass form-control"
                              value={followUpDateInputText}
                              onChange={handleFollowUpDateChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="d-flex align-items-md-center">
                          <Form.Group controlId="meeting" className="mt-md-3">
                            <Form.Check
                              type="checkbox"
                              label="Meeting"
                              checked={!!contactDetails.meeting}
                              onChange={handleInputChange("meeting")}
                            />
                          </Form.Group>
                        </Col>
                      </>
                    )}
                  </DialogSection>

                  <DialogSection
                    title="Email & Tracking"
                    summary={[
                      getEmailStatus(contactDetails).label,
                      formatDate(contactDetails.date_sent) &&
                      `Last Sent ${formatDate(contactDetails.date_sent)}`,
                      `${contactDetails.view_count_direct ??
                      contactDetails.view_count ??
                      0} prospect views`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    open={openSections.tracking}
                    onToggle={() => toggleSection("tracking")}
                  >
                    <Col md={6}>
                      <Form.Group controlId="emailSent">
                        <Form.Check
                          type="checkbox"
                          label="Email Sent"
                          checked={contactDetails.email_sent}
                          onChange={handleInputChange("email_sent")}
                        />
                        {/* Read-only: written by the send and by Resend's
                            delivery webhook, never by this form. */}
                        {contactDetails.email_status && (() => {
                          const status = getEmailStatus(contactDetails);
                          const when = formatDate(contactDetails.email_status_date);
                          return (
                            <div className="mt-1">
                              <small className="text-muted">Delivery: </small>
                              <span className={`badge ${status.badgeClass}`}>
                                {status.label}
                              </span>
                              {when && (
                                <small className="text-muted"> {when}</small>
                              )}
                              {status.detail && (
                                <small
                                  className={`d-block ${status.problem ? "text-danger" : "text-muted"}`}
                                >
                                  {status.detail}
                                </small>
                              )}
                            </div>
                          );
                        })()}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="liveContact">
                        <Form.Check
                          type="checkbox"
                          label="Live Contact"
                          checked={contactDetails.live_contact !== false}
                          onChange={handleInputChange("live_contact")}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="dateSent">
                        <Form.Label>Most Recent Date Sent</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="MM-DD-YYYY"
                          className="inputclass form-control"
                          value={dateSentInputText}
                          onChange={(e) => {
                            const text = e.target.value;
                            setDateSentInputText(text);
                            const parsed = parseTypedDate(text);
                            if (parsed) {
                              setContactDetails((prev) => ({
                                ...prev,
                                date_sent: parsed,
                              }));
                            } else if (text.trim() === "") {
                              setContactDetails((prev) => ({
                                ...prev,
                                date_sent: "",
                              }));
                            }
                          }}
                        />
                        {!contactDetails.date_sent && (
                          <small className="text-muted">
                            {contactDetails.email_sent
                              ? "Unknown — type a date to set it."
                              : "Not yet sent."}
                          </small>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="demoId">
                        <Form.Label>Demo ID</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Demo ID"
                          className="inputclass form-control"
                          value={contactDetails.demo_id}
                          onChange={handleInputChange("demo_id")}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="viewCountDirect">
                        <Form.Label>Prospect Views</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          placeholder="0"
                          className="inputclass form-control"
                          value={
                            contactDetails.view_count_direct ??
                            contactDetails.view_count ??
                            0
                          }
                          onChange={(e) => {
                            const raw = e.target.value;
                            const parsed = raw === "" ? 0 : parseInt(raw, 10);
                            setContactDetails((prev) => ({
                              ...prev,
                              view_count_direct: Number.isNaN(parsed) ? 0 : parsed,
                            }));
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="viewCountAdmin">
                        <Form.Label>Admin Views</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          placeholder="0"
                          className="inputclass form-control"
                          value={contactDetails.view_count_admin ?? 0}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const parsed = raw === "" ? 0 : parseInt(raw, 10);
                            setContactDetails((prev) => ({
                              ...prev,
                              view_count_admin: Number.isNaN(parsed) ? 0 : parsed,
                            }));
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </DialogSection>

                  <DialogSection
                    title="Unsubscribed"
                    summary={
                      contactDetails.unsubscribed
                        ? ["Unsubscribed", formatDate(contactDetails.unsubscribed_date)]
                          .filter(Boolean)
                          .join(" · ")
                        : "Subscribed"
                    }
                    open={openSections.unsubscribed}
                    onToggle={() => toggleSection("unsubscribed")}
                  >
                    <Col md={12}>
                      <Form.Group controlId="unsubscribed">
                        <Form.Check
                          type="checkbox"
                          label="Unsubscribed"
                          checked={!!contactDetails.unsubscribed}
                          onChange={handleUnsubscribedChange}
                        />
                      </Form.Group>
                    </Col>
                    {contactDetails.unsubscribed && (
                      <>
                        <Col md={8}>
                          <Form.Group controlId="unsubscribedReason">
                            <Form.Label>Why did they unsubscribe?</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              placeholder="Explain why they unsubscribed"
                              className="inputclass form-control"
                              value={contactDetails.unsubscribed_reason || ""}
                              onChange={handleInputChange("unsubscribed_reason")}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group controlId="unsubscribedDate">
                            <Form.Label>Unsubscribed On</Form.Label>
                            {/* Stamped by the server when the box is first saved as
                                checked, so it's blank until then. */}
                            <p className="mb-0">
                              {formatDate(contactDetails.unsubscribed_date) || (
                                <span className="text-muted">
                                  Will be set when you save
                                </span>
                              )}
                            </p>
                          </Form.Group>
                        </Col>
                      </>
                    )}
                  </DialogSection>
                </Col>

                {/* Notes sit beside the sections on large screens, so they stay
                    in view whichever sections are open, and stretch to the
                    sections' full height. Stacked on small screens, the rows
                    setting alone decides the height. */}
                <Col lg={5} className="d-flex flex-column">
                  <Form.Group
                    controlId="notes"
                    className="d-flex flex-column flex-grow-1"
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label className="mb-0">
                        <strong>Notes</strong>
                      </Form.Label>
                      {!showAddNote && (
                        <div>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => setShowAddNote(true)}
                            className="p-0 mr-3"
                            style={{ textDecoration: "none" }}
                          >
                            + Add Note
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => setNotesEditable((v) => !v)}
                            className="p-0"
                            style={{ textDecoration: "none" }}
                          >
                            {notesEditable ? "Lock Notes" : "Edit Notes"}
                          </Button>
                        </div>
                      )}
                    </div>
                    {showAddNote && (
                      <div className="mb-2 p-2 border rounded">
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Type new note — date and time will be added automatically"
                          className="inputclass form-control mb-2"
                          value={newNoteText}
                          onChange={(e) => setNewNoteText(e.target.value)}
                          autoFocus
                        />
                        <div className="text-right">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleAddNoteCancel}
                            className="mr-2"
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddNoteSave}
                            disabled={!newNoteText.trim() || saving}
                          >
                            {saving ? "Saving..." : "Add"}
                          </Button>
                        </div>
                      </div>
                    )}
                    <Form.Control
                      as="textarea"
                      rows={16}
                      placeholder="Notes"
                      className="inputclass form-control flex-grow-1"
                      value={contactDetails.notes}
                      onChange={handleInputChange("notes")}
                      readOnly={!notesEditable}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </form>
          </div>
          {/* Sticky so Save stays reachable however far down the dialog is
              scrolled. */}
          <div
            className="message-footer text-right bg-white border-top"
            style={{ display: "block", position: "sticky", bottom: 0, zIndex: 1 }}
          >
            <Button
              variant="warning"
              title="Save"
              type="button"
              size="xs"
              color="white"
              onClick={saveContactHandler}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </Modal>

      <SendMarketingEmailConfirmModal
        show={showSendConfirm}
        contact={contactToSend}
        recipient={recipientToSend}
        lastSent={lastSentLabel(contactToSend, recipientToSend)}
        // Every send from the open Edit dialog saves the whole contact.
        savesChanges={show}
        sending={sending}
        onCancel={handleSendCancel}
        onConfirm={handleSendConfirm}
      />

      <CopyDemoLinkModal
        show={contactForLinks !== null}
        contact={contactForLinks}
        onClose={() => setContactForLinks(null)}
      />

      {/* Unsaved Note Warning Modal */}
      <Modal
        show={pendingNoteAction !== null}
        onHide={handleKeepEditingNote}
        centered
        className="custome-modal"
      >
        <div className="message-container custome-msg-container">
          <div className="message-header-box">
            <h4>Note Not Added</h4>
            <div className="messages-modal-actions">
              <Button className="btn" onClick={handleKeepEditingNote}>
                <FaTimes />
              </Button>
            </div>
          </div>
          <div className="message-body">
            <p>
              You have typed a note but have not clicked <strong>Add</strong>{" "}
              yet.
            </p>
            <p className="text-danger mb-0">
              {pendingNoteAction === "save"
                ? "Saving the contact will not include this note, and it will be discarded."
                : "Closing this dialog will discard the note."}
            </p>
          </div>
          <div
            className="message-footer text-right"
            style={{ display: "block" }}
          >
            <Button
              variant="secondary"
              type="button"
              size="xs"
              onClick={handleKeepEditingNote}
              className="mr-2"
            >
              Go Back
            </Button>
            <Button
              variant="danger"
              type="button"
              size="xs"
              onClick={handleDiscardNote}
            >
              {pendingNoteAction === "save"
                ? "Discard Note and Save"
                : "Discard Note and Close"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={handleDeleteCancel}
        centered
        className="custome-modal"
      >
        <div className="message-container custome-msg-container">
          <div className="message-header-box">
            <h4>Confirm Delete</h4>
            <div className="messages-modal-actions">
              <Button className="btn" onClick={handleDeleteCancel}>
                <FaTimes />
              </Button>
            </div>
          </div>
          <div className="message-body">
            <p>
              Are you sure you want to delete the contact for{" "}
              <strong>{contactToDelete?.group_name}</strong>?
            </p>
            <p className="text-danger">This action cannot be undone.</p>
          </div>
          <div
            className="message-footer text-right"
            style={{ display: "block" }}
          >
            <Button
              variant="secondary"
              type="button"
              size="xs"
              onClick={handleDeleteCancel}
              disabled={deleting}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              type="button"
              size="xs"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </React.Fragment>
  );
}
